import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { formatCOP, formatDate } from '@/lib/format';
import { Eye, ShieldAlert, CheckCircle2, Clock, Send, Ban, Undo2 } from 'lucide-react';

const STATUS_META = {
  pending_delivery: { label: 'Pendiente entrega', cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
  ready:            { label: 'Listo',              cls: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  scheduled:        { label: 'Programado',         cls: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Send },
  sent:             { label: 'Enviado',            cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Send },
  completed:        { label: 'Completado',         cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  held:             { label: 'Bloqueado',          cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: ShieldAlert },
  refunded:         { label: 'Reembolsado',        cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Undo2 },
  failed:           { label: 'Falló',              cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: Ban },
};

const HELD_REASON_LABEL = {
  kyc_pending: 'Datos bancarios pendientes',
  open_refund: 'Reembolso en curso',
  open_pqr: 'PQR abierta',
  order_not_delivered: 'Pedido no entregado',
};

export function SellerPayoutsSection() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('all');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 25 };
      if (status !== 'all') params.status = status;
      const { data } = await api.get('/sellers/me/payouts', { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/sellers/me/payouts/${id}`);
    setDetail(data);
  };

  return (
    <section className="mt-8" data-testid="seller-payouts-timeline">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">Ciclo de pago</div>
          <h2 className="font-display text-lg font-black tracking-tighter text-slate-900">Timeline de retiros por pedido</h2>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="seller-payouts-status-filter" className="rounded-none w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.keys(STATUS_META).map((s) => (
              <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">Pedido</th>
              <th className="px-3 py-2">Producto</th>
              <th className="px-3 py-2 text-right">Bruto</th>
              <th className="px-3 py-2 text-right">Comisión</th>
              <th className="px-3 py-2 text-right">Neto</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (<tr><td colSpan={7} className="p-8 text-center text-slate-500">Cargando…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={7} className="p-8 text-center text-slate-500">Aún sin payouts</td></tr>)}
            {items.map((p) => {
              const meta = STATUS_META[p.status] || { label: p.status, cls: 'bg-slate-100' };
              return (
                <tr key={p.payout_id} className="hover:bg-slate-50" data-testid={`seller-payout-row-${p.payout_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.order_id.slice(0, 12)}…</td>
                  <td className="px-3 py-2 text-xs truncate max-w-[220px]">{p.order_product || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatCOP(p.gross)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{formatCOP(p.commission)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{formatCOP(p.net)}</td>
                  <td className="px-3 py-2">
                    <Badge className={`rounded-none border ${meta.cls}`}>{meta.label}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      data-testid={`seller-payout-view-${p.payout_id}`}
                      onClick={() => openDetail(p.payout_id)}
                      className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                    >
                      <Eye className="h-3 w-3" /> Timeline
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Timeline del pago</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-slate-900">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><div className="text-[10px] font-bold uppercase text-slate-500">Pedido</div><div className="font-mono text-xs">{detail.payout.order_id}</div></div>
                <div><div className="text-[10px] font-bold uppercase text-slate-500">Estado</div><Badge className={`rounded-none border ${(STATUS_META[detail.payout.status] || {}).cls}`}>{(STATUS_META[detail.payout.status] || {}).label || detail.payout.status}</Badge></div>
                <div><div className="text-[10px] font-bold uppercase text-slate-500">Bruto</div><div className="font-mono">{formatCOP(detail.payout.gross)}</div></div>
                <div><div className="text-[10px] font-bold uppercase text-slate-500">Comisión AutoMarket</div><div className="font-mono">{formatCOP(detail.payout.commission)}</div></div>
                <div><div className="text-[10px] font-bold uppercase text-slate-500">Tu neto</div><div className="font-mono font-bold text-emerald-700">{formatCOP(detail.payout.net)}</div></div>
                {detail.payout.gateway_transaction_id && (
                  <div className="col-span-2">
                    <div className="text-[10px] font-bold uppercase text-slate-500">Referencia Wompi</div>
                    <div className="font-mono text-xs break-all">{detail.payout.gateway_transaction_id}</div>
                  </div>
                )}
                {detail.payout.status === 'held' && (
                  <div className="col-span-2 bg-rose-50 border border-rose-200 p-2 text-xs">
                    <b>Motivo de bloqueo:</b>{' '}
                    {HELD_REASON_LABEL[(detail.payout.history || []).slice().reverse().find(h => h.status === 'held')?.note] || 'Bajo revisión'}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase text-slate-500 mb-2">Historial</div>
                <ol className="border-l-2 border-slate-200 pl-4 space-y-3">
                  {(detail.payout.history || []).map((h, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[19px] top-1 h-3 w-3 rounded-full bg-slate-900" />
                      <div className="text-xs">
                        <span className="font-bold">{(STATUS_META[h.status] || {}).label || h.status}</span>
                        <span className="ml-2 text-slate-500">{formatDate(h.at)}</span>
                      </div>
                      {h.note && !h.note.startsWith('wompi.') && (
                        <div className="text-xs text-slate-600">{HELD_REASON_LABEL[h.note] || h.note}</div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
